import { useEffect, useState } from "react";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom";
import { Button, Table } from "reactstrap";
import {
  applyClassToCharacters,
  CheckPermission,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { GetLanguageString, NoResult } from "../../helper/Components";
import { GetCustomStyles, GetSelectedLanguage } from "../../helper/Method";
import ApiFinanceConfig from "./ApiFinanceConfig";
import EditFinanceConfig from "./EditFinanceConfig";

export const STANDARD_FINANCE_KEYS = [
  {
    value: "muqami_tanzeem_expense_limit",
    label: "Muqami Tanzeem Expense Limit",
    urduLabel: "مقامی تنظیم خرچہ کی حد",
    description: "Maximum allowable expense limit for Muqami Tanzeem units.",
    urduDescription: "مقامی تنظیم کے یونٹس کے لیے زیادہ سے زیادہ خرچہ کی حد۔",
  },
  {
    value: "muqami_tanzeem_cash_limit",
    label: "Muqami Tanzeem Cash Limit",
    urduLabel: "مقامی تنظیم کیش کی حد",
    description: "Maximum allowable cash-in-hand limit for Muqami Tanzeem units.",
    urduDescription: "مقامی تنظیم کے یونٹس کے لیے نقد رقم رکھنے کی حد۔",
  },
  {
    value: "halqa_expense_limit",
    label: "Halqa Expense Limit",
    urduLabel: "حلقہ خرچہ کی حد",
    description: "Maximum allowable expense limit for Halqa units.",
    urduDescription: "حلقہ کے یونٹس کے لیے زیادہ سے زیادہ خرچہ کی حد۔",
  },
  {
    value: "halqa_cash_limit",
    label: "Halqa Cash Limit",
    urduLabel: "حلقہ کیش کی حد",
    description: "Maximum allowable cash-in-hand limit for Halqa units.",
    urduDescription: "حلقہ کے یونٹس کے لیے نقد رقم رکھنے کی حد۔",
  },
  {
    value: "expense_amount_limit",
    label: "Expense Approval Amount Limit",
    urduLabel: "خرچہ منظوری کی حد",
    description: "Threshold amount above which expense requests require approval attachment.",
    urduDescription: "خرچہ کی وہ حد جس سے زائد رقم پر شوریٰ منظوری ضروری ہے۔",
  },
  {
    value: "digital_receipt_counter",
    label: "Digital Receipt Counter",
    urduLabel: "ڈیجیٹل رسید نمبر کاؤنٹر",
    description: "Running sequence counter used to generate official digital receipt numbers.",
    urduDescription: "سرکاری ڈیجیٹل رسید نمبر جاری کرنے کے لیے تسلسلی کاؤنٹر۔",
    isCounter: true,
  },
  {
    value: "usra_cash_limit",
    label: "Usra Cash Holding Limit",
    urduLabel: "اسرہ کیش رکھنے کی حد",
    description: "Maximum cash amount an Usra is allowed to hold before remittance.",
    urduDescription: "رقم مرکز بھیجنے سے قبل اسرہ کے پاس نقد رقم رکھنے کی حد۔",
  },
  {
    value: "usra_cash_holding_days",
    label: "Usra Cash Holding Days",
    urduLabel: "اسرہ کیش رکھنے کے دن",
    description: "Maximum consecutive days an Usra can hold collected cash before remitting.",
    urduDescription: "رقم مرکز بھیجنے سے قبل اسرہ کے پاس رقم رکھنے کے زیادہ سے زیادہ دن۔",
  },
];

const FinanceConfig = ({ history }) => {
  const unitTypeName = encryptStorage.getItem("unitTypeName");
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const isMarkaz = unitTypeName === "Markaz" || loggedInUnitId === 0;
  const hasPermission = isMarkaz && CheckPermission("CanManageFinanceConfig");

  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [configList, setConfigList] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});

  useEffect(() => {
    if (!hasPermission) {
      history.push("/funds");
      return;
    }

    setLoading(true);
    ApiFinanceConfig()
      .GetAll()
      .then((res) => {
        setConfigList(res.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, [refresh, hasPermission, history]);

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setEditModal(true);
  };

  return (
    <>
      {editModal ? (
        <EditFinanceConfig
          selectedProgress={selectedRecord}
          setEditModal={setEditModal}
          refresh={refresh}
          setRefresh={setRefresh}
        />
      ) : null}
      <div className="wrapper wrapper-content wrapper-custom">
        {!loading ? (
          <div className="ibox">
            <div className="ibox-title">
              <h5>
                <GetLanguageString props="finance_tab_finance_config" />
              </h5>
              <Button
                className={GetCustomStyles().btn_style}
                color="white"
                size="xs"
                onClick={() => history.push("/funds")}
              >
                <IoArrowBackSharp
                  color="#18A689"
                  size={15}
                  style={{ marginRight: 5, marginBottom: 1 }}
                />
                <GetLanguageString props="courses_participants_back" />
              </Button>
            </div>
            <div className="ibox-content">
              {configList && configList.length !== 0 ? (
                <div className="table-responsive">
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>
                          <GetLanguageString props="finance_config_key" />
                        </th>
                        <th>
                          <GetLanguageString props="finance_config_value" />
                        </th>
                        <th className="text-center">
                          <GetLanguageString props="common_action" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {configList.map((record, index) => {
                        const stdMeta = STANDARD_FINANCE_KEYS.find(
                          (k) => k.value.toLowerCase() === record.key?.toLowerCase()
                        );
                        const isCounter = stdMeta?.isCounter === true;
                        const isUr = GetSelectedLanguage() === "ur";
                        const titleUrdu = stdMeta?.urduLabel || "";
                        const titleEnglish = stdMeta?.label || record.key;
                        const subDescription = isUr
                          ? stdMeta?.urduDescription || stdMeta?.description || ""
                          : stdMeta?.description || "";

                        return (
                          <tr key={index}>
                            <td className="p-t-custom">{index + 1}</td>
                            <td>
                              <div style={{ fontWeight: 600, fontSize: "13.5px", color: "#2f4050" }}>
                                {isUr ? (
                                  <span>{applyClassToCharacters(titleUrdu || titleEnglish, "ur")}</span>
                                ) : (
                                  <span>{titleEnglish}</span>
                                )}
                              </div>
                              <div style={{ fontSize: "11.5px", color: "#666", marginTop: "3px" }}>
                                {subDescription ? (
                                  <span style={{ marginRight: "6px" }}>
                                    {applyClassToCharacters(subDescription, "ur")}
                                  </span>
                                ) : null}
                                <code style={{ fontSize: "11px", color: "#1a6b5c", backgroundColor: "#f0f9f8", padding: "1px 5px", borderRadius: "3px" }}>
                                  {record.key}
                                </code>
                              </div>
                            </td>
                            <td style={{ verticalAlign: "middle", fontWeight: 600, fontSize: "14px" }}>
                              {applyClassToCharacters(record.value, "ur")}
                            </td>
                            <td className="text-center">
                              {isCounter ? (
                                <span className="text-muted" style={{ fontSize: "12px", fontStyle: "italic" }}>
                                  <GetLanguageString props="finance_config_auto_counter" />
                                </span>
                              ) : (
                                <Button
                                  color="white"
                                  size="sm"
                                  onClick={() => handleEdit(record)}
                                >
                                  <GetLanguageString props="common_edit" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <NoResult />
              )}
            </div>
          </div>
        ) : (
          LoadingSpinner()
        )}
      </div>
    </>
  );
};

export default withRouter(FinanceConfig);
